import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AnalyticsEvents } from '../../../enums/AnalyticsEvents';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { useLiveSync } from '../../../hooks/useLiveSync';
import { AgentType } from '../../../interfaces/Agent';
import { DateRangeDate } from '../../../interfaces/Analytics';
import { DeviceWidth } from '../../../interfaces/App';
import VerticalStack from '../../../styles/VerticalStack';
import DateRangePicker from '../../shared/DateRangePicker/DateRangePicker';
import LiveSyncStatus from '../../shared/LiveSyncToggle/LiveSyncStatus';
import LiveSyncToggle from '../../shared/LiveSyncToggle/LiveSyncToggle';
import { ALL_AGENTS } from '../../../constants/dashboardConstants';
import FilterByAgentDropdown from './FilterByAgentDropdown';
import MultiCallAgentsDashboard from './MultiCallAgentsDashboard';
import MultiChatAgentsDashboard from './MultiChatAgentsDashboard';
import StyledFilters from './StyledFilters';

interface MultiAgentDashboardProps {
  type: AgentType;
  dateRange?: [DateRangeDate, DateRangeDate];
  setDateRange?: (range: [DateRangeDate, DateRangeDate]) => void;
}

const MultiAgentDashboard: React.FC<MultiAgentDashboardProps> = ({
  type = AgentType.CALL,
  dateRange: propDateRange,
  setDateRange: propSetDateRange,
}) => {
  const { track } = useAnalytics();

  // Use provided date range or fall back to local state
  const [localDateRange, setLocalDateRange] = useState<
    [DateRangeDate, DateRangeDate]
  >([dayjs().subtract(14, 'day'), dayjs()]);

  const dateRange = propDateRange ?? localDateRange;
  const setDateRange = propSetDateRange ?? setLocalDateRange;
  const [selectedAgent, setSelectedAgent] = useState<string>(ALL_AGENTS);

  // Store refetch function from child component for live sync
  const dashboardRefetchRef = useRef<(() => void) | null>(null);
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);

  const handleRefetchNeeded = (refetch: () => void) => {
    dashboardRefetchRef.current = refetch;
  };

  const handleLoadingStateChange = (isLoading: boolean) => {
    setIsLoadingDashboard(isLoading);
  };

  const refetchDashboard = useCallback(() => {
    dashboardRefetchRef.current?.();
  }, []);
  const liveSync = useLiveSync({
    refetch: refetchDashboard,
    isUpdating: isLoadingDashboard,
  });

  // Track date range changes
  useEffect(() => {
    track(AnalyticsEvents.DATE_RANGE_CHANGED, {
      startDate: dateRange[0]?.format('YYYY-MM-DD'),
      endDate: dateRange[1]?.format('YYYY-MM-DD'),
    });
  }, [dateRange, track]);

  return (
    <VerticalStack maxWidth={DeviceWidth.LG}>
      {/* Filters at the top */}
      <StyledFilters>
        <DateRangePicker
          startDate={dateRange[0]}
          endDate={dateRange[1]}
          onChange={setDateRange}
        />
        <FilterByAgentDropdown onChange={setSelectedAgent} agentType={type} />
        <LiveSyncToggle
          enabled={liveSync.isEnabled}
          onChange={liveSync.setIsEnabled}
          disabled={isLoadingDashboard}
        />
      </StyledFilters>
      {liveSync.isEnabled && (
        <LiveSyncStatus
          isUpdating={liveSync.isUpdating}
          secondsSinceUpdate={liveSync.secondsSinceUpdate}
        />
      )}
      {type === AgentType.CALL ? (
        <MultiCallAgentsDashboard
          dateRange={dateRange}
          selectedAgent={selectedAgent}
          showLimitModal={liveSync.showLimitModal}
          setShowLimitModal={liveSync.setShowLimitModal}
          onRefetchNeeded={handleRefetchNeeded}
          onLoadingStateChange={handleLoadingStateChange}
        />
      ) : (
        <MultiChatAgentsDashboard
          dateRange={dateRange}
          selectedAgent={selectedAgent}
          showLimitModal={liveSync.showLimitModal}
          setShowLimitModal={liveSync.setShowLimitModal}
          onRefetchNeeded={handleRefetchNeeded}
          onLoadingStateChange={handleLoadingStateChange}
        />
      )}
    </VerticalStack>
  );
};

export default MultiAgentDashboard;
